import React, { PureComponent } from 'react'

import { ProductItem } from 'OrderSummary/ProductItem'
import { RecipeItem } from 'OrderSummary/RecipeItem'
import { SaveButton } from 'OrderSummary/SaveButton'
import { ProductItem as LegacyProductItem } from 'Product/ProductItem'
import classnames from 'classnames'
import Immutable from 'immutable'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'

import { Icon } from 'components/Icon'
import { Item } from 'components/Item'
import { Receipt } from 'components/Receipt'
import { UserCreditMessage } from 'components/UserCreditMessage'
import { deliveryPriceConfig } from 'config/deliveryPrice'
import { isNotAGift } from 'utils/products'
import { useWelcomeTracking } from 'routes/Welcome/hooks/useWelcomeTracking'

import css from './OrderSummary.css'

const propTypes = {
  canEditOrder: PropTypes.bool,
  prices: ImmutablePropTypes.map,
  deliveryDate: PropTypes.string.isRequired,
  deliverySlot: PropTypes.instanceOf(Immutable.Map),
  giftItems: PropTypes.instanceOf(Immutable.Map),
  numPortions: PropTypes.number.isRequired,
  numRecipes: PropTypes.number.isRequired,
  productItems: PropTypes.instanceOf(Immutable.Map),
  products: ImmutablePropTypes.map.isRequired,
  recipeItems: PropTypes.instanceOf(Immutable.Map),
  recipes: ImmutablePropTypes.map.isRequired,
  removeProduct: PropTypes.func,
  shippingAddress: ImmutablePropTypes.map,
  saveError: PropTypes.bool,
  saveRequired: PropTypes.bool,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  orderNumber: PropTypes.string,
  orderSummaryCollapsed: PropTypes.bool,
  isOrderConfirmation: PropTypes.bool.isRequired,
  onOrderConfirmationMobile: PropTypes.bool.isRequired,
  shouldRedirectToMyGousto: PropTypes.bool,
}

const defaultProps = {
  canEditOrder: true,
  prices: Immutable.Map({}),
  deliverySlot: Immutable.Map(),
  giftItems: Immutable.Map(),
  productItems: Immutable.Map(),
  recipeItems: Immutable.Map(),
  removeProduct: null,
  shippingAddress: Immutable.Map(),
  saveError: false,
  saveRequired: false,
  saving: false,
  orderNumber: '',
  orderSummaryCollapsed: true,
  shouldRedirectToMyGousto: false,
}

class OrderSummary extends PureComponent {
  asterisk = String.fromCharCode(42)

  constructor(props) {
    super(props)
    this.state = {
      orderSummaryOpen: false,
    }
  }

  onOrderSave = () => {
    const { isOrderConfirmation, onSave, shouldRedirectToMyGousto } = this.props
    onSave({ isOrderConfirmation, shouldRedirectToMyGousto })
  }

  toggleDetailView = () => {
    const { orderSummaryOpen } = this.state
    const { trackUnfoldSummary } = useWelcomeTracking()

    this.setState({
      orderSummaryOpen: !orderSummaryOpen,
    })
    trackUnfoldSummary({ isOpen: !orderSummaryOpen })
  }

  getProducts = () => {
    const { productItems, products, removeProduct } = this.props

    return productItems
      .map((productQty, productId) => {
        const product = products.get(productId, Immutable.Map())

        return {
          orderItemId: productId,
          title: product.get('title'),
          disclaimerKey: product.get('isVatable') ? this.asterisk : '',
          quantity: parseFloat(productQty),
          images: product.get('images'),
          onRemove: removeProduct
            ? () => {
                removeProduct(productId)
              }
            : undefined,
        }
      })
      .toArray()
  }

  getProductGifts = () => {
    const { giftItems, products } = this.props

    return giftItems
      .map((productQty, productId) => {
        const product = products.get(productId, Immutable.Map())

        // Hide gifts
        if (!isNotAGift(product)) {
          return false
        }

        return {
          orderItemId: productId,
          title: product.get('title'),
          quantity: parseFloat(productQty),
          images: product.get('images'),
          gift: true,
        }
      })
      .toArray()
      .filter((item) => item)
  }

  getRecipes = () => {
    const { recipeItems, recipes } = this.props
    const recipesDetails = []

    recipeItems.forEach((r) => {
      const recipeId = r.get('itemableId')
      const recipeQty = r.get('quantity')
      const recipe = recipes.get(recipeId, Immutable.Map())
      const shelfLife = recipe.get('shelfLife')

      if (recipe.has('title') && recipe.has('media')) {
        recipesDetails.push({
          orderItemId: recipeId,
          title: recipe.get('title'),
          numPortions: parseFloat(recipeQty),
          media: recipe.get('media'),
          shelfLife: Immutable.Map.isMap(shelfLife) ? shelfLife.toJS() : shelfLife,
          url: recipe.get('url'),
        })
      }
    })

    return recipesDetails
  }

  renderFooter = () => {
    const { orderSummaryOpen } = this.state

    return (
      <footer className={classnames(css.mobileOnly, css.footer)}>
        {orderSummaryOpen ? (
          <button type="button" className={css.toggleLink} onClick={this.toggleDetailView}>
            Hide order details
            <Icon name="fa-angle-up" className={css.footerIcon} />
          </button>
        ) : (
          <button type="button" className={css.toggleLink} onClick={this.toggleDetailView}>
            View order details
            <Icon name="fa-angle-down" className={css.footerIcon} />
          </button>
        )}
      </footer>
    )
  }

  render() {
    const {
      canEditOrder,
      prices,
      deliveryDate,
      deliverySlot,
      numPortions,
      numRecipes,
      shippingAddress,
      productItems,
      products,
      orderNumber,
      saving,
      saveRequired,
      saveError,
      orderSummaryCollapsed,
      onOrderConfirmationMobile,
      isOrderConfirmation,
    } = this.props
    const { orderSummaryOpen } = this.state
    let vatableItemsInOrder = false
    let extrasPrice = 0.0
    let totalToPay = prices.get('total') - prices.get('productTotal')
    const deliveryPrice = deliveryPriceConfig.deliveryPriceFormatted

    productItems.forEach((productQty, productId) => {
      const product = products.get(productId, Immutable.Map())

      if (product.get('isVatable')) {
        vatableItemsInOrder = true
      }

      extrasPrice += productQty * parseFloat(product.get('listPrice'))
      totalToPay += productQty * parseFloat(product.get('listPrice'))
    })

    return (
      <section className={css.container} data-testid="order-summary-component-section">
        <div
          className={classnames(
            css.details,
            { [css.slideUp]: !orderSummaryOpen && orderSummaryCollapsed },
            { [css.mobileHide]: !orderSummaryOpen && orderSummaryCollapsed },
          )}
        >
          {isOrderConfirmation
            ? this.getRecipes().map((recipe) => (
                <RecipeItem
                  key={recipe.orderItemId}
                  boxPortions={numPortions}
                  deliveryDate={deliveryDate}
                  media={recipe.media.getIn(['images', 0, 'urls'], Immutable.List([]))}
                  numPortions={recipe.numPortions}
                  shelfLife={recipe.shelfLife}
                  title={recipe.title}
                />
              ))
            : this.getRecipes().map((recipe) => (
                <Item
                  key={recipe.orderItemId}
                  type="recipe"
                  fromBox={recipe.fromBox}
                  media={recipe.media.getIn(['images', 0, 'urls'], Immutable.List([]))}
                  onImageClick={null}
                  quantity={recipe.numPortions}
                  recipeId={recipe.recipeId}
                  title={recipe.title}
                  url={recipe.url}
                  available
                  canEditOrder={canEditOrder}
                />
              ))}
          {isOrderConfirmation
            ? this.getProducts().map((product) => (
                <ProductItem
                  key={product.orderItemId}
                  canEditOrder={canEditOrder}
                  disclaimerKey={product.disclaimerKey}
                  images={product.images}
                  onRemove={product.onRemove}
                  quantity={product.quantity}
                  title={product.title}
                />
              ))
            : this.getProducts().map((product) => (
                <LegacyProductItem
                  key={product.orderItemId}
                  disclaimerKey={product.disclaimerKey}
                  images={product.images}
                  title={product.title}
                  quantity={product.quantity}
                  onRemove={product.onRemove}
                  available
                  canEditOrder={canEditOrder}
                />
              ))}
          {isOrderConfirmation
            ? this.getProductGifts().map((product) => (
                <ProductItem
                  key={product.orderItemId}
                  canEditOrder={canEditOrder}
                  gift={product.gift}
                  images={product.images}
                  quantity={product.quantity}
                  title={product.title}
                />
              ))
            : this.getProductGifts().map((product) => (
                <LegacyProductItem
                  key={product.orderItemId}
                  images={product.images}
                  title={product.title}
                  quantity={product.quantity}
                  gift={product.gift}
                  available
                  canEditOrder={canEditOrder}
                />
              ))}

          <div className={css.receipt}>
            <Receipt
              prices={prices}
              deliveryDate={deliveryDate}
              deliverySlot={deliverySlot}
              numPortions={numPortions}
              numRecipes={numRecipes}
              deliveryTotalPrice={prices.get('deliveryTotal')}
              isDeliveryFree={prices.get('isDeliveryFree')}
              shippingAddress={shippingAddress}
              vatableItems={vatableItemsInOrder}
              surchargeCount={prices.get('surchargeCount')}
              surchargeTotal={prices.get('surchargeTotal')}
              recipeTotalPrice={prices.get('recipeTotal')}
              totalToPay={String(totalToPay)}
              recipeDiscountAmount={prices.get('recipeDiscount')}
              recipeDiscountPercent={prices.get('percentageOff')}
              extrasTotalPrice={String(extrasPrice)}
              orderNumber={orderNumber}
              flatDeliveryFee={deliveryPrice}
            >
              {vatableItemsInOrder ? (
                <p className={css.disclaimer}>{this.asterisk} These items include VAT at 20%</p>
              ) : null}
            </Receipt>
          </div>
          <UserCreditMessage />
          <div
            className={classnames({ [css.updateOrderButtonSummary]: onOrderConfirmationMobile })}
          >
            <SaveButton
              disabled={!canEditOrder}
              saving={saving}
              saveRequired={saveRequired}
              onClick={this.onOrderSave}
              error={saveError}
            />
          </div>
        </div>
        {orderSummaryCollapsed ? this.renderFooter() : null}
      </section>
    )
  }
}

OrderSummary.propTypes = propTypes

OrderSummary.defaultProps = defaultProps

export default OrderSummary
